import React, { useEffect, useState } from 'react';
import { XIcon, AlertTriangleIcon, CheckCircleIcon } from 'lucide-react';
interface PrintSizeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (printSize: number, authorized: boolean) => void;
  imageDimensions: {
    width: number;
    height: number;
  };
  currentPrintSize?: number;
  currentAuthorization?: boolean;
}
export function PrintSizeModal({
  isOpen,
  onClose,
  onConfirm,
  imageDimensions,
  currentPrintSize,
  currentAuthorization = false
}: PrintSizeModalProps) {
  const [printSize, setPrintSize] = useState(currentPrintSize || 6);
  const [authorized, setAuthorized] = useState(currentAuthorization);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  useEffect(() => {
    if (currentPrintSize) setPrintSize(currentPrintSize);
    setAuthorized(currentAuthorization);
  }, [currentPrintSize, currentAuthorization]);
  if (!isOpen) return null;
  const effectiveDPI = imageDimensions.width / printSize;
  const printHeight = imageDimensions.height / imageDimensions.width * printSize;
  const needsWarning = effectiveDPI < 300;
  const warningLevel = needsWarning && !authorized ? 'red' : needsWarning && authorized ? 'yellow' : 'green';
  const handleConfirm = () => {
    onConfirm(printSize, authorized);
    onClose();
  };
  const handleFixResolution = () => {
    setShowAuthDialog(true);
  };
  const handleAuthorize = () => {
    setAuthorized(true);
    setShowAuthDialog(false);
  };
  return <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-323">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-id="element-324">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200" data-id="element-325">
            <h3 className="text-lg font-semibold text-gray-900" data-id="element-326">
              Set Print Size
            </h3>
            <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-327">
              <XIcon className="w-5 h-5" data-id="element-328" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4" data-id="element-329">
            <p className="text-sm text-gray-600" data-id="element-330">
              Set the actual size this design will be printed at. This is
              separate from the visual preview size.
            </p>

            {/* Print Size Input */}
            <div data-id="element-331">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-332">
                Print Width (inches)
              </label>
              <input type="number" min="0.5" max="24" step="0.25" value={printSize} onChange={e => setPrintSize(parseFloat(e.target.value) || 1)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-333" />
              <p className="text-xs text-gray-500 mt-1" data-id="element-334">
                Height: {printHeight.toFixed(2)}" (proportional)
              </p>
            </div>

            {/* Resolution Info */}
            <div className={`p-4 rounded-lg border-2 ${warningLevel === 'red' ? 'bg-red-50 border-red-300' : warningLevel === 'yellow' ? 'bg-yellow-50 border-yellow-300' : 'bg-green-50 border-green-300'}`} data-id="element-335">
              <div className="flex items-start gap-3" data-id="element-336">
                {warningLevel === 'green' ? <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-337" /> : <AlertTriangleIcon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${warningLevel === 'red' ? 'text-red-600' : 'text-yellow-600'}`} data-id="element-338" />}
                <div className="flex-1" data-id="element-339">
                  <h4 className={`text-sm font-semibold mb-1 ${warningLevel === 'red' ? 'text-red-900' : warningLevel === 'yellow' ? 'text-yellow-900' : 'text-green-900'}`} data-id="element-340">
                    {warningLevel === 'green' ? 'Resolution OK' : 'Resolution Warning'}
                  </h4>
                  <div className="text-xs space-y-1" data-id="element-341">
                    <p className={warningLevel === 'red' ? 'text-red-800' : warningLevel === 'yellow' ? 'text-yellow-800' : 'text-green-800'} data-id="element-342">
                      <strong data-id="element-343">Image:</strong> {imageDimensions.width} ×{' '}
                      {imageDimensions.height} px
                    </p>
                    <p className={warningLevel === 'red' ? 'text-red-800' : warningLevel === 'yellow' ? 'text-yellow-800' : 'text-green-800'} data-id="element-344">
                      <strong data-id="element-345">Print Size:</strong> {printSize}" ×{' '}
                      {printHeight.toFixed(2)}"
                    </p>
                    <p className={warningLevel === 'red' ? 'text-red-800' : warningLevel === 'yellow' ? 'text-yellow-800' : 'text-green-800'} data-id="element-346">
                      <strong data-id="element-347">Effective DPI:</strong> {Math.round(effectiveDPI)}{' '}
                      DPI
                    </p>
                  </div>

                  {needsWarning && <p className={`text-xs mt-2 ${warningLevel === 'red' ? 'text-red-700' : 'text-yellow-700'}`} data-id="element-348">
                      {warningLevel === 'red' ? <>
                          ⚠️ Your image resolution is too low for this print
                          size. Stretching a{' '}
                          {(imageDimensions.width / 300).toFixed(2)}" image to{' '}
                          {printSize}" drops the DPI to{' '}
                          {Math.round(effectiveDPI)} DPI. This will not print
                          well.
                        </> : <>
                          ✓ Resolution fix authorized. We will enhance your
                          artwork for optimal print quality.
                        </>}
                    </p>}

                  {needsWarning && !authorized && <button onClick={handleFixResolution} className="mt-3 w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors" data-id="element-349">
                      Fix Resolution ($35 fee)
                    </button>}

                  {needsWarning && authorized && <div className="mt-3 px-4 py-2 bg-yellow-100 border border-yellow-300 rounded-lg text-center" data-id="element-350">
                      <p className="text-xs font-medium text-yellow-900" data-id="element-351">
                        ✓ Resolution Fix Authorized
                      </p>
                    </div>}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 p-4 border-t border-gray-200" data-id="element-352">
            <button onClick={onClose} className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors" data-id="element-353">
              Cancel
            </button>
            <button onClick={handleConfirm} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors" data-id="element-354">
              Confirm Print Size
            </button>
          </div>
        </div>
      </div>

      {/* Authorization Dialog */}
      {showAuthDialog && <div className="fixed inset-0 bg-black bg-opacity-60 z-[60] flex items-center justify-center p-4" data-id="element-355">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6" data-id="element-356">
            <div className="text-center" data-id="element-357">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-358">
                <AlertTriangleIcon className="w-6 h-6 text-blue-600" data-id="element-359" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-360">
                Authorize Resolution Fix
              </h3>
              <p className="text-sm text-gray-600 mb-6" data-id="element-361">
                I authorize you to resolve this issue for a fee of{' '}
                <strong data-id="element-362">$35.00</strong>.
              </p>
              <div className="flex gap-3" data-id="element-363">
                <button onClick={() => setShowAuthDialog(false)} className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors" data-id="element-364">
                  Cancel
                </button>
                <button onClick={handleAuthorize} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors" data-id="element-365">
                  Authorize
                </button>
              </div>
            </div>
          </div>
        </div>}
    </>;
}